import { Box, Stack, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { ArrowUpRight } from 'lucide-react'
import { featuredProducts } from '@/content/products'
import { colors, fonts, layout } from '@/theme'
import { Container } from '@/components/ui/Container'
import { Eyebrow } from '@/components/ui/Eyebrow'
import { Media } from '@/components/ui/Media'
import { ComingSoonMedia } from '@/components/ui/ComingSoonMedia'

const items = featuredProducts(6)

/**
 * Premium featured-products presentation — a horizontal snap rail of spotlit
 * product cards. Each product sits under a soft gold spotlight and lifts on hover.
 */
export function ProductSpotlight() {
  return (
    <Box component="section" sx={{ py: layout.sectionPaddingY }}>
      <Container>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-end' }} spacing={2} sx={{ mb: { xs: 4, md: 6 } }}>
          <Box>
            <Eyebrow>Featured</Eyebrow>
            <Typography variant="h2" component="h2">Trusted by professionals</Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Swipe to explore →
          </Typography>
        </Stack>
      </Container>

      {/* Full-bleed rail — spans the viewport; first card aligns to the container's left edge */}
      <Box
        sx={{
          display: 'flex',
          gap: { xs: 2.5, md: 3 },
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          scrollPaddingLeft: { xs: 24, sm: 32, md: `max(40px, calc((100% - ${layout.containerMaxWidth}px) / 2 + 40px))` },
          pb: 2,
          pl: { xs: 3, sm: 4, md: `max(40px, calc((100% - ${layout.containerMaxWidth}px) / 2 + 40px))` },
          pr: { xs: 3, sm: 4, md: 5 },
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
          {items.map((p) => (
            <Box
              key={p.slug}
              component={RouterLink}
              to={`/products/${p.category}/${p.slug}`}
              sx={{
                flex: '0 0 auto',
                width: { xs: '78vw', sm: 320, md: 380 },
                scrollSnapAlign: 'start',
                textDecoration: 'none',
                '&:hover .ps-media img': { transform: 'scale(1.06)' },
                '&:hover .ps-frame': { borderColor: 'primary.main', transform: 'translateY(-6px)' },
                '&:hover .ps-arrow': { color: colors.gold, transform: 'translate(2px, -2px)' },
              }}
            >
              <Box
                className="ps-frame"
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 1,
                  border: `1px solid ${colors.border}`,
                  bgcolor: colors.surfaceAlt,
                  transition: 'border-color 300ms ease, transform 400ms cubic-bezier(.2,.8,.2,1)',
                }}
              >
                {/* Soft spotlight behind the product */}
                <Box aria-hidden sx={{ position: 'absolute', inset: 0, zIndex: 0, background: 'radial-gradient(60% 55% at 50% 40%, rgba(212,175,55,0.12), transparent 70%)' }} />
                <Box className="ps-media" sx={{ position: 'relative', zIndex: 1, '& img': { transition: 'transform 650ms cubic-bezier(.2,.8,.2,1)' } }}>
                  {p.thumbnail ? (
                    <Media src={p.thumbnail.src} srcSm={p.thumbnail.srcSm} alt={p.name} width={p.thumbnail.width} height={p.thumbnail.height} ratio={1} sizes="(max-width:600px) 78vw, 380px" />
                  ) : (
                    <ComingSoonMedia />
                  )}
                </Box>
                {/* Model number watermark */}
                <Typography aria-hidden sx={{ position: 'absolute', zIndex: 1, right: 14, bottom: 8, fontFamily: fonts.display, fontWeight: 600, fontSize: 40, lineHeight: 1, color: 'rgba(245,245,245,0.06)', pointerEvents: 'none' }}>
                  {p.modelNumber}
                </Typography>
              </Box>

              <Stack sx={{ mt: 2.5 }} spacing={0.75}>
                <Typography variant="overline" sx={{ color: 'primary.main' }}>{p.modelNumber}</Typography>
                <Typography variant="h5" component="h3">{p.name}</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>{p.shortDescription}</Typography>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1, color: 'text.secondary' }}>
                  <Typography variant="subtitle2" component="span">View details</Typography>
                  <ArrowUpRight size={16} className="ps-arrow" style={{ transition: 'color 300ms ease, transform 300ms ease' }} />
                </Stack>
              </Stack>
            </Box>
          ))}
      </Box>
    </Box>
  )
}
